import { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Collapse,
  IconButton,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { formatCurrency } from "../../../../../utils/mockWalletData";

/**
 * CustomerTopupVerification Tab
 * Shows pending customer top-up requests awaiting bank verification
 * Thaklis Admin can APPROVE or REJECT
 */

function ExpandableRow({ topup, onApprove, onReject }) {
  const [open, setOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [notes, setNotes] = useState("");

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "URGENT":
        return "error";
      case "HIGH":
        return "warning";
      case "MEDIUM":
        return "info";
      case "LOW":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING_BANK_VERIFICATION":
        return "warning";
      default:
        return "default";
    }
  };

  const handleActionOpen = (type) => {
    setActionType(type);
    setActionOpen(true);
    setNotes("");
  };

  const handleActionConfirm = () => {
    if (actionType === "APPROVE") {
      onApprove(topup.id, notes);
    } else if (actionType === "REJECT") {
      onReject(topup.id, notes);
    }
    setActionOpen(false);
    setActionType(null);
    setNotes("");
  };

  const handleActionCancel = () => {
    setActionOpen(false);
    setActionType(null);
    setNotes("");
  };

  return (
    <>
      <TableRow
        sx={{
          "&:hover": { bgcolor: "grey.50" },
          borderLeft: `4px solid ${
            topup.priority === "HIGH" ? "#ff6f00" : "#2196f3"
          }`,
        }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: "bold" }}>
          {topup.customerName}
        </TableCell>
        <TableCell>{formatCurrency(topup.amount)}</TableCell>
        <TableCell>
          <Chip
            label={topup.paymentMethod}
            size="small"
            variant="outlined"
            color="primary"
          />
        </TableCell>
        <TableCell>
          <Chip
            label={`${topup.daysWaiting}d`}
            size="small"
            color={topup.daysWaiting > 2 ? "error" : "default"}
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <Chip
            label={topup.status}
            size="small"
            color={getStatusColor(topup.status)}
          />
        </TableCell>
        <TableCell>
          <Chip
            label={topup.priority}
            size="small"
            color={getPriorityColor(topup.priority)}
          />
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleActionOpen("APPROVE")}
              sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}
            >
              Approve
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => handleActionOpen("REJECT")}
              sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}
            >
              Reject
            </Button>
          </Box>
        </TableCell>
      </TableRow>

      {/* Expandable Details */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, bgcolor: "grey.50", p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2 }}>
                Bank Transfer Details
              </Typography>

              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Bank Name
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {topup.bankDetails?.bankName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Account Number
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {topup.bankDetails?.accountNumber}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Reference Number
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {topup.bankDetails?.referenceNumber}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Transfer Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {topup.bankDetails?.transferDate}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ bgcolor: "white", p: 1.5, borderRadius: 1, mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: "bold", display: "block", mb: 0.5 }}>
                  Money Status:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ✓ In Thaklis bank, awaiting system recording
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                <strong>Attachments:</strong>
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {topup.attachments?.map((att) => (
                  <Chip
                    key={att.id}
                    label={att.fileName}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Action Dialog */}
      <Dialog open={actionOpen} onClose={handleActionCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === "APPROVE"
            ? "Approve Customer Top-up"
            : "Reject Customer Top-up"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity={actionType === "APPROVE" ? "success" : "error"} sx={{ mb: 2 }}>
              <Typography variant="body2">
                {actionType === "APPROVE"
                  ? `Approve ${formatCurrency(topup.amount)} for ${topup.customerName}?`
                  : `Reject ${formatCurrency(topup.amount)} for ${topup.customerName}?`}
              </Typography>
            </Alert>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes (optional)"
              placeholder={
                actionType === "APPROVE"
                  ? "Add verification notes..."
                  : "Reason for rejection..."
              }
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleActionCancel}>Cancel</Button>
          <Button
            onClick={handleActionConfirm}
            variant="contained"
            color={actionType === "APPROVE" ? "success" : "error"}
          >
            {actionType === "APPROVE" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const CustomerTopupVerification = ({ data }) => {
  const handleApprove = (topupId, notes) => {
    console.log("Approved topup:", topupId, "Notes:", notes);
    // TODO: Call API to approve
  };

  const handleReject = (topupId, notes) => {
    console.log("Rejected topup:", topupId, "Notes:", notes);
    // TODO: Call API to reject
  };

  if (!data || data.items.length === 0) {
    return (
      <Alert severity="info">
        <Typography variant="body2">
          No pending customer top-up verifications at this time.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, p: 1.5, bgcolor: "success.light", borderRadius: 1, border: "1px solid", borderColor: "success.lighter" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold", color: "success.dark" }}>
          Total Pending: {formatCurrency(data.total)} ({data.count} request{data.count > 1 ? "s" : ""})
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "primary.main" }}>
            <TableRow>
              <TableCell width="40" sx={{ fontWeight: "bold", color: "white" }} />
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Customer Name</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold", color: "white" }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Method</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Days</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Priority</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((topup) => (
              <ExpandableRow
                key={topup.id}
                topup={topup}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CustomerTopupVerification;
